import * as THREE from 'three';

const FLOATS_PER_VERTEX = 11; // pos(3) + normal(3) + uv(2) + color(3)

export function createChunkGeometry(
  vertices: Float32Array,
  indices: Uint32Array
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();

  const ib = new THREE.InterleavedBuffer(vertices, FLOATS_PER_VERTEX);
  geometry.setAttribute('position', new THREE.InterleavedBufferAttribute(ib, 3, 0, false));
  geometry.setAttribute('normal',   new THREE.InterleavedBufferAttribute(ib, 3, 3, false));
  geometry.setAttribute('uv',       new THREE.InterleavedBufferAttribute(ib, 2, 6, false));
  geometry.setAttribute('color',    new THREE.InterleavedBufferAttribute(ib, 3, 8, false));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));

  return geometry;
}

// Returns false if the geometry shape differs and the caller must re-create.
// Never recomputes the bounding sphere — a section's extents are fixed by its
// (cx, sy, cz), so the caller sets a static sphere once at creation.
export function updateChunkGeometry(
  geometry: THREE.BufferGeometry,
  vertices: Float32Array,
  indices: Uint32Array
): boolean {
  const posAttr = geometry.getAttribute('position');
  if (!(posAttr instanceof THREE.InterleavedBufferAttribute)) return false;

  const oldIb = posAttr.data as THREE.InterleavedBuffer;

  if (oldIb.array.length === vertices.length) {
    (oldIb.array as Float32Array).set(vertices);
    oldIb.needsUpdate = true;
  } else {
    const ib = new THREE.InterleavedBuffer(vertices, FLOATS_PER_VERTEX);
    geometry.setAttribute('position', new THREE.InterleavedBufferAttribute(ib, 3, 0, false));
    geometry.setAttribute('normal',   new THREE.InterleavedBufferAttribute(ib, 3, 3, false));
    geometry.setAttribute('uv',       new THREE.InterleavedBufferAttribute(ib, 2, 6, false));
    geometry.setAttribute('color',    new THREE.InterleavedBufferAttribute(ib, 3, 8, false));
  }

  const idx = geometry.getIndex();
  if (idx && idx.array.length === indices.length) {
    (idx.array as Uint32Array).set(indices);
    idx.needsUpdate = true;
  } else {
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  }

  return true;
}
